/* eslint-disable @typescript-eslint/camelcase */
import axios from 'axios';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { createStyles, Theme, makeStyles, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import { LinkedIn } from '@material-ui/icons';
import { colors } from '../../theme/theme';
import { get, cn, getConfig, getText, getUpdatedLink } from '../../helpers';
import HtmlContent from '../_common/html-content';
import { ReactComponent as ClearIcon } from '../../assets/svg/icons/close.svg';
import { ReactComponent as SearchIcon } from '../../assets/svg/icons/search.svg';
import { ReactComponent as InfoIcon } from '../../assets/svg/icons/info-small.svg';
import { ReactComponent as ThumbUpIcon } from '../../assets/svg/icons/thumb-up.svg';
import { ReactComponent as ThumbDownIcon } from '../../assets/svg/icons/thumb-down.svg';
import { ReactComponent as ThumbUpColorIcon } from '../../assets/svg/icons/thumb-up-filled.svg';
import { ReactComponent as MagicWandIcon } from '../../assets/svg/icons/retouch_magic_wand.svg';
import APIManager from '../../utilities/api-manager';
import Modal from '../_common/modal';
import keyPages from '../../utilities/key-pages';
import RequestTrialFormProvider from '../_providers/rat-form-provider/rat-form-provider';
import CustomLink from '../_common/custom-link';
import SPGVariables from '../../utilities/spg-variables';
import { ReactComponent as KenshoIcon } from '../../assets/svg/icons/kensho.svg';
import { ReactComponent as CaretRightIcon } from '../../assets/svg/icons/caret-right.svg';

const spgVariables = SPGVariables();

const useStyles = makeStyles(() => ({
    textField: {
        '& :before': {
            content: 'none'
        },
        '& :after': {
            content: 'none'
        }
    }
}));

type RelatedLink = {
    link_category: string;
    name: string;
    url: string;
};

const Window = window as AnonymousObject;
Window.dataLayer = Window.dataLayer || [];
const infoIconTooltip = getText('pages.llm.tooltip');
const aiPoweredText = getText('pages.llm.aiPoweredSearchText');
const logInToAccessAiText = getText('pages.llm.logInToAccessAi');
const searchErrorMessage = getText('pages.llm.errorMessage');
const langCode = getText('langCode');
const userGuideText = getText('pages.llm.userGuide');
const dataGuideText = getText('pages.llm.dataGuide');
const supportArticleText = getText('pages.llm.supportArticle');
const techGuideText = getText('pages.llm.techGuide');
const learnMoreText = getText('pages.llm.learnMoreText');
const learnMoreUrl = getText('pages.llm.learnMoreUrl');
const kenshoIconTooltip = getText('pages.llm.tooltip');
const maxSearchTextError = getText('pages.llm.maxSearchTextError');
const env = spgVariables.ENV;

const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: colors.white,
        color: colors.black,
        maxWidth: theme.typography.pxToRem(400),
        fontSize: theme.typography.pxToRem(14),
        border: `${theme.typography.pxToRem(1)} solid ${colors.grey80}`
    }
}))(Tooltip);

const ChatiqSearch = (props: ChatiqSearchProps) => {
    const { text, placeholder, variant, id, isInternal, dataProfile, isLoggedIn } = props;
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [maxCharErrMsg, setMaxCharErrMsg] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showResult, setShowResult] = React.useState<string>('');
    const [response, setResponse] = React.useState<any>();
    const [feedback, setFeedback] = React.useState<string>('');
    const [modalIsOpen, setModalOpen] = React.useState<boolean>(false);
    const [relatedLinks, setRelatedLinks] = React.useState<any[]>();
    const [redIconClicked, setRedIconClicked] = React.useState<boolean>(false);
    const [isTextArea, setIsTextArea] = React.useState(false);
    const [maxChars, setMaxChars] = React.useState(20);
    const [callToActionLinks, seCallToActionLinks] = React.useState<JSX.Element[]>([]);
    const inputRef = React.useRef(null);
    const signInUrl = getConfig('urls.mi.ssacs.oktaSignIn');
    let returnUrl = '';

    const onCancelClick = () => {
        setSearchValue('');
        setShowResult('');
        setFeedback('');
        setMaxCharErrMsg('');
        APIManager.cancelApiRequest();
    };

    const activateTextAreaOrInput = () => {
        const focusElement = () => {
            setTimeout(() => {
                const inp: any = inputRef.current;
                if (inp) {
                    const l: any = inp.value.length;
                    inp.focus();
                    inp.setSelectionRange(l, l);
                }
            }, 100);
        };
        if (searchValue.length > maxChars) {
            setIsTextArea(true);
            focusElement();
        } else if (isTextArea) {
            setIsTextArea(false);
            focusElement();
        }
    };

    const handleOnChange = (event: React.ChangeEvent) => {
        const { value } = event.currentTarget as HTMLInputElement;
        if (feedback !== '') setFeedback('');
        const regex = /^\s+/g;
        const realValue = value.replace(regex, '');
        if (realValue.length > 500) {
            setMaxCharErrMsg(maxSearchTextError);
        } else if (maxCharErrMsg.length > 0) setMaxCharErrMsg('');
        setSearchValue(realValue);
        if (!realValue) {
            setShowResult('');
            if (isLoading) setIsLoading(false);
        }
    };

    const handleBlur = () => {
        setIsTextArea(false);
    };

    const variantData = () => {
        switch (variant) {
            case 'dataset':
                return {
                    dataProvider: (dataProfile as DataProfile).dataProvider,
                    productVariant: variant,
                    productName: (dataProfile as DataProfile).name,
                    productId: (dataProfile as DataProfile).id.toString(),
                    titleText: (dataProfile as DataProfile).marketplaceTrialButtonTitle,
                    marketMSADataLink: (dataProfile as DataProfile).marketMSADataLink,
                    dataFilePath: (dataProfile as DataProfile).marketMSADataFilePath,
                    marketplaceMSATooltip: (dataProfile as DataProfile).marketplaceMSATooltip
                };
            case 'bundle':
                return {
                    dataProvider: (dataProfile as DataProfile).dataProvider,
                    productVariant: variant,
                    productName: (dataProfile as DataProfile).name,
                    productId: (dataProfile as DataProfile).id.toString(),
                    titleText: (dataProfile as DataProfile).marketplaceTrialButtonTitle,
                    marketMSADataLink: (dataProfile as DataProfile).marketMSADataLink,
                    dataFilePath: (dataProfile as DataProfile).marketMSADataFilePath,
                    marketplaceMSATooltip: (dataProfile as DataProfile).marketplaceMSATooltip
                };
            case 'solution':
                return {
                    dataProvider: (dataProfile as APISolution).MarketSolutionProvider.MktplaceSolutionProvider,
                    productVariant: variant,
                    productName: (dataProfile as APISolution).MktplaceSolutionName,
                    productId: (dataProfile as APISolution).KeyPlatformService,
                    titleText: (dataProfile as APISolution).MarketplaceTrialButtonTitle,
                    marketMSADataLink: (dataProfile as APISolution).MSADataLink,
                    dataFilePath: (dataProfile as APISolution).MSADataFilePath,
                    marketplaceMSATooltip: (dataProfile as APISolution).MarketplaceMSATooltip
                };
            case 'blueprint':
                return {
                    dataProvider: (dataProfile as BluePrintProfile).MarketplaceDataProviders != null && (dataProfile as BluePrintProfile).MarketplaceDataProviders.length > 0 && (dataProfile as BluePrintProfile).MarketplaceDataProviders[0] ? (dataProfile as BluePrintProfile).MarketplaceDataProviders[0].MarketplaceDataProvider : '',
                    productVariant: variant,
                    productName: (dataProfile as BluePrintProfile).MarketplaceBlueprintName,
                    productId: (dataProfile as BluePrintProfile).KeyMarketplaceBlueprint,
                    titleText: (dataProfile as BluePrintProfile).MarketplaceTrialButtonTitle,
                    marketMSADataLink: (dataProfile as BluePrintProfile).MSADataLink,
                    dataFilePath: (dataProfile as BluePrintProfile).MSADataFilePath,
                    marketplaceMSATooltip: (dataProfile as BluePrintProfile).MarketplaceMSATooltip
                };
            default: {
                return {
                    dataProvider: '',
                    productVariant: '',
                    productName: '',
                    productId: '',
                    titleText: '',
                    marketMSADataLink: '',
                    dataFilePath: '',
                    marketplaceMSATooltip: undefined
                };
            }
        }
    };

    const {
        dataProvider,
        productVariant,
        productName,
        productId,
        titleText,
        marketMSADataLink,
        dataFilePath,
        marketplaceMSATooltip
    } = variantData();

    const getUserType = () => {
        const internalExternal = isInternal ? 'internal' : 'external';
        const userType = isLoggedIn ? internalExternal : 'not_authenticated';
        return userType;
    };

    const getQueryData = () => {
        const data = {
            query: searchValue,
            user_type: getUserType(),
            pk_marketplace_dataset: variant === 'dataset' ? id as number : undefined,
            pk_marketplace_dataset_bundle: variant === 'bundle' ? id as number : undefined,
            pk_marketplace_platform_service: variant === 'solution' ? id as string : undefined,
            pk_marketplace_blueprint: variant === 'blueprint' ? id as string : undefined
        };
        return data;
    };

    const populatRelatedLink = (valueList: any[]) => {
        const itemId = typeof id === 'number' ? id.toString() : id;
        const regex = new RegExp(`/.*\\(${itemId}\\)`);
        if (valueList && valueList.length > 0) {
            const newList = valueList.map(item => {
                return Object.values(item)[0];
            });
            const validLinks = newList ? newList.filter((link: any) => !regex.test(link.tile_url)) : [];
            const top4Links = validLinks.slice(0, validLinks.length > 4 ? 4 : validLinks.length);
            setRelatedLinks(top4Links);
        } else setRelatedLinks([]);
    };

    const handleError = (isRequestManuallyCanceled = false) => {
        setIsLoading(false);
        setRedIconClicked(false);
        setResponse(null);
        setRelatedLinks([]);
        if (!isRequestManuallyCanceled) {
            setShowResult(searchErrorMessage);
        }
    };

    const onLinkClicked = (value: string, isSkip: boolean) => {
        const link = isSkip ? value : getUpdatedLink(value, langCode);
        if (link) {
            Window.dataLayer.push({
                event: 'suggestionButtonLLM',
                suggestionButtonURL: link,
            });
            window.open(link, '_blank');
        }
    };

    const responseVaidation = (data: any) => {
        if (data && data.related_links) {
            const actions: RelatedLink[] = []; // callToAction buttons objects
            const filteredLinks: RelatedLink[] = data.related_links.filter((link: any) => link.url !== undefined && link.link_category !== undefined); // valid links
            // get call-to-action links according to relevancy scores
            // filteredLinks are already ordered acoording to relevancy
            // we just need to get first object from each category and not loose order
            filteredLinks.forEach((link: RelatedLink) => {
                const cat = link.link_category;
                if ((cat === 'tile_user_guide' || cat === 'data_guide' || cat === 'knowledge_base') && !actions.some(el => el.link_category === cat)) {
                    actions.push(link);
                }
            });
            if (actions.length) {
                // call-to-action button element
                const actionButton = (key: string, url: string, name: string) => {
                    return (
                        <button type="button" key={key} data-testid="user-guide-btn" className="chatiq-search__link-group-btn" onClick={() => onLinkClicked(url, true)}>
                            <span>{name}</span>
                        </button>
                    );
                };
                seCallToActionLinks(
                    actions.map((el: RelatedLink) => {
                        switch (el.link_category) {
                            case 'tile_user_guide':
                                return actionButton(el.link_category, el.url, (el.url.includes('/datasets/') || el.url.includes('/blueprints/')) ? userGuideText : techGuideText);
                            case 'data_guide':
                                return actionButton(el.link_category, el.url, dataGuideText);
                            case 'knowledge_base':
                                return actionButton(el.link_category, el.url, supportArticleText);
                            default:
                                return <></>;
                        }
                    })
                );
            }
        }
        const validLinks = data.related_links ? data.related_links.filter((link: any) => link.url === undefined) : [];
        populatRelatedLink(validLinks && validLinks.length > 0 ? validLinks : []);
    };

    const emailStatusCallback = (hasRequestSuccess: boolean, successCallback: () => void, error: (error: string) => void) => {
        if (hasRequestSuccess) {
            successCallback();
        } else {
            error('Email request  failed');
        }
    };

    // Example success callback function
    const handleSuccess = () => {
        console.log('Email request succeeded');
    };

    // Example error callback function
    const handleEmailError = (errorMessage: any) => {
        console.error(`Email request failed: ${errorMessage}`);
    };

    const sendEmail = (errorBody: string, errorSubject: string) => {
        APIManager.sendEmail(
            errorBody,
            errorSubject,
            spgVariables.KENSHOLLMEmailList,
            (isSuccess: boolean) => {
                emailStatusCallback(isSuccess, handleSuccess, handleEmailError);
            }
        );
    };

    const sendSearchFailedEmail = (value: string, reason: any) => {
        const errorData = '<br>SearchValue - '.concat(value);
        const errorText = '<br>Reason - '.concat(JSON.stringify(reason));
        const errorBody = 'Gen AI Search service error: '.concat(errorData).concat(errorText);
        const errorSubject = (env ? env.toUpperCase().concat(' - ') : '').concat('Marketplace Gen AI Search Service Error');
        sendEmail(errorBody, errorSubject);
    };

    const onSearchIconClick = () => {
        if (searchValue.length > 500) return;

        if (searchValue) {
            Window.dataLayer.push({
                event: 'searchSubmitLLM',
                searchQuestion: searchValue,
            });
            setFeedback('');
            setIsLoading(true);
            setRedIconClicked(true);
            const data = getQueryData();
            APIManager.getLLMAccessToken().then(token => {
                APIManager.getChatiqSearchById(data, token && token.data && token.data.AccessToken ? token.data.AccessToken : '').then(res => {
                    setIsLoading(false);
                    setRedIconClicked(false);
                    if (res.data) {
                        if (res.data.response_uuid && res.data.response) {
                            responseVaidation(res.data);
                            setResponse(res.data);
                            if (res.data.response) setShowResult(res.data.response);
                        } else {
                            setShowResult(searchErrorMessage);
                            setResponse(null);
                            setRelatedLinks([]);
                            sendSearchFailedEmail(searchValue, res.data);
                        }
                    }
                }).catch(error => {
                    const isCanceled = axios.isCancel(error);
                    handleError(isCanceled);
                    sendSearchFailedEmail(searchValue, error && error.message ? error.message : 'Search Service Error');
                });
            }).catch(er => {
                const isReqCanceled = axios.isCancel(er);
                handleError(isReqCanceled);
            });
        }
    };

    const handleOnKeyDown = (event: any) => {
        if (event.keyCode === 13) {
            onSearchIconClick();
        }
        if (event.keyCode === 27) {
            onCancelClick();
        }
    };

    const sendFeedbackFailedEmail = (data: any, reason: any) => {
        console.log('Gen AI Feedback service error, data=', JSON.stringify(data));
        const errorData = '<br>Data - '.concat(JSON.stringify(data));
        const errorText = '<br>Reason - '.concat(JSON.stringify(reason));
        const errorBody = 'Gen AI Feedback service error:'.concat(errorData).concat(errorText);
        const errorSubject = (env ? env.toUpperCase().concat(' - ') : '').concat('Marketplace Gen AI Feedback Service Error');
        sendEmail(errorBody, errorSubject);
    };

    const sendFeedback = (value: string) => {
        const responseId = response && response.response_uuid;
        const data = {
            response_uuid: responseId,
            feedback_flag: value
        };
        APIManager.getLLMAccessToken().then(token => {
            APIManager.sendChatiqSearchFeedbackById(data, token && token.data && token.data.AccessToken ? token.data.AccessToken : '').then(res => {
                if (!(res.status === 200 && res.data === '')) {
                    sendFeedbackFailedEmail(data, res.data);
                }
            }).catch(error => {
                sendFeedbackFailedEmail(data, error && error.message ? error.message : 'Feedback Service Error');
            });
        }).catch(() => {
            console.log('Gen AI Feedback service access token error, data=', JSON.stringify(data));
        });
    };

    const onThumbUpButtonClick = () => {
        setFeedback('positive');
        sendFeedback('positive');
    };

    const onThumbDownButtonClick = () => {
        setFeedback('negative');
        sendFeedback('negative');
    };

    const getThumbUpIcon = () => {
        if (feedback === 'positive') return <ThumbUpColorIcon aria-label="thumb-up-selected" />;
        return <ThumbUpIcon data-testid="thumb-up-btn" aria-label="thumb-up-not-selected" />;
    };

    const getThumbDownIcon = () => {
        if (feedback === 'negative') return <ThumbUpColorIcon className="chatiq-search__thumbup_rotate" aria-label="thumb-down-selected" />;
        return <ThumbDownIcon data-testid="thumb-down-btn" aria-label="thumb-down-not-selected" />;
    };

    const getKeyPageId = (): keyPages => {
        if (variant === 'solution') {
            return keyPages.solutionRATForm;
        }
        return keyPages.datasetRATForm;
    };

    const onLoginClick = (event: React.SyntheticEvent) => {
        if (process.env.NODE_ENV === 'production') {
            if (window.location.pathname.length > 1) {
                returnUrl = `&mpurl=${encodeURIComponent(window.location.pathname)}`;
            }
            event.preventDefault();
            window.location.href = `${signInUrl + returnUrl}`;
        } else {
            window.location.href = signInUrl;
        }
    };

    const classes = useStyles();

    // max number of characters till input text overflow
    // 6.5 is estimated avarage char width for current font size
    const updateMaxChars = () => {
        if (inputRef.current) {
            const inp: any = inputRef.current;
            const estimatedMaxChars = Math.floor(inp.offsetWidth) / 6.5 - 10;
            setMaxChars(estimatedMaxChars);
        }
    };

    React.useEffect(() => {
        updateMaxChars();
    });

    const showToolTipIcon = () => {
        return (
            <HtmlTooltip
                placement="top"
                arrow
                tabIndex={0}
                aria-label="info tooltip"
                role="button"
                title={
                    <>
                        <HtmlContent className="tooltip-content" html={kenshoIconTooltip} />
                    </>
                }
            >
                <KenshoIcon className="profile__kenshoicon" />
            </HtmlTooltip>
        );
    };

    return (
        <div data-testid="chatiqsearch" className={`chatiq-search ${isLoggedIn ? 'logged-in' : ''}`}>
            <div className="chatiq-search__title">
                <div className="chatiq-search__titleTextGrp">
                    <span className="chatiq-search__paragraph p--small">{text}</span>
                </div>
                {(
                    <button
                        onClick={onCancelClick}
                        type="button"
                        className="button button--icon-only chatiq-search__clearicon"
                        aria-label="clear-icon"
                        data-testid="clear-icon"
                        id="clearicon-btn"
                    >
                        <ClearIcon />
                    </button>
                )}
            </div>
            {(
                <div className="chatiq-search__aipowertitle">
                    <span className="chatiq-search__aipowertext">{aiPoweredText}</span>
                    <CustomLink className="chatiq-search__aipowertext__link" hasIcon text={learnMoreText} hasUnderline url={getUpdatedLink(learnMoreUrl, langCode)} />
                </div>
            )}
            {(
                <div id="chatiq-search__bar" className="chatiq-search__bar">
                    {showToolTipIcon()}
                    <Waypoint>
                        <TextField
                            type="search"
                            id="chatiq-search-bar"
                            inputRef={inputRef}
                            autoComplete="off"
                            fullWidth
                            multiline={isTextArea}
                            onChange={handleOnChange}
                            onBlur={handleBlur}
                            onFocus={activateTextAreaOrInput}
                            onKeyDown={handleOnKeyDown}
                            value={searchValue}
                            placeholder={placeholder}
                            className={cn(classes.textField, 'chatiq-search__input')}
                            inputProps={{
                                'aria-label': placeholder,
                                'data-testid': 'search-input'
                            }}
                        />
                    </Waypoint>
                    <div className="chatiq-search__icon_group">
                        {searchValue && searchValue.length > 0 ?
                            (
                                <button type="button" disabled={redIconClicked} data-testid="chatiq-search-redicon-btn" className={cn('button button--icon-only button--primary', redIconClicked ? 'chatiq-search__redicon-disabled' : 'chatiq-search__redicon')} aria-label="search-icon" onClick={onSearchIconClick}>
                                    <SearchIcon />
                                </button>
                            ) : (
                                <button type="button" id="chatiq-search-icon-btn" className="button button--icon-only button--secondary chatiq-search__icon" aria-label="search-icon" onClick={onSearchIconClick}>
                                    <SearchIcon />
                                </button>
                            )}
                    </div>
                </div>
            )}
            {maxCharErrMsg && maxCharErrMsg.length > 0 && (<span className="chatiq-search__error-message">{maxCharErrMsg}</span>)}
            {isLoading && (
                <div className="chatiq-search__search_loading">
                    <div className="chatiq-search__loading-text" role="alert">Generating...</div>
                    <div className="chatiq-search__loading_bar" />
                    <div className="chatiq-search__loading_bar" />
                    <div className="chatiq-search__loading_bar" />
                </div>
            )}
            {!isLoading && showResult && showResult.length > 0 && (
                <>
                    <div className="chatiq-search__result-panel">
                        <div className="chatiq-search__result-summary" role="alert">{showResult.replace('\n1', '\n\n1')}</div>
                    </div>
                    <div className="chatiq-search__button-link-group">
                        <div className="chatiq-search__link-group-first">
                            <button type="button" className="chatiq-search__link-group-btn" aria-label="talk-to-an-expert" onClick={() => setModalOpen(true)}>
                                <span>Talk to an Expert</span>
                            </button>
                            {isLoggedIn ? (
                                callToActionLinks
                            ) : (
                                <a
                                    href={signInUrl}
                                    target="_self"
                                    rel="noopener noreferrer"
                                    data-testid="chatiq-search__titleTextGrp--login-link"
                                    className="chatiq-search__link-group-btn chatiq-search__sign-in-btn"
                                    onClick={onLoginClick}
                                >
                                    <span>Sign In to Marketplace</span>
                                </a>
                            )}
                        </div>
                        {response && (
                            <div className="chatiq-search__link-group-first">
                                <button type="button" className="chatiq-search__thumb-up-icon" onClick={onThumbUpButtonClick}>
                                    {getThumbUpIcon()}
                                </button>
                                <button type="button" className="chatiq-search__thumb-down-icon" onClick={onThumbDownButtonClick}>
                                    {getThumbDownIcon()}
                                </button>
                            </div>
                        )}
                    </div>
                    {relatedLinks && relatedLinks.length > 0 && (
                        <>
                            <h2 className="chatiq-search__button-link-group-addl-text">Related Datasets, Solutions and Blueprints</h2>
                            <div className="chatiq-search__button-link-group-addl">
                                {relatedLinks && relatedLinks.map(link => <button type="button" key={typeof id === 'number' ? (link.id).toString() : link.id} className="chatiq-search__link-group-btn" onClick={() => onLinkClicked(link.tile_url, false)}><span>{link.name}</span></button>)}
                            </div>
                        </>
                    )}
                </>
            )}
            <Modal
                open={modalIsOpen}
                onClose={() => setModalOpen(false)}
                isFullScreen={window.innerWidth < 1024}
                scrollBody
                keyPageId={getKeyPageId()}
                title="request more information"
            >
                <RequestTrialFormProvider
                    dataProvider={dataProvider}
                    productVariant={productVariant}
                    productName={productName}
                    productId={productId}
                    titleText={titleText}
                    marketMSADataLink={marketMSADataLink}
                    dataFilePath={dataFilePath}
                    marketplaceMSATooltip={marketplaceMSATooltip}
                    ratVariant="talktoexpert"
                />
            </Modal>
        </div>
    );
};

export default ChatiqSearch;
